import React, { useEffect, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { abandonedCartApi } from "../api/abandonedCarts";
import { formatPrice } from "../utils/pricing";
import { Badge, Card, Spinner, Button } from "../components/common/UIKit";
import { formatDistanceToNow } from "date-fns";
import { X } from "lucide-react";

export default function AdminAbandonedCartsPage() {
  const [carts, setCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCart, setSelectedCart] = useState(null);

  useEffect(() => {
    abandonedCartApi.listAdminCarts()
      .then((data) => {
        setCarts(
          data.sort(
            (a, b) =>
              new Date(b.last_activity_at) - new Date(a.last_activity_at)
          )
        );
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getStatusBadge = (status) => {
    if (status === "recovered") return <Badge color="green">Recovered</Badge>;
    if (status === "expired") return <Badge color="red">Expired</Badge>;
    return <Badge color="amber">Pending</Badge>;
  };

  const getIdentity = (cart) => {
    if (cart.full_name && cart.email)
      return `${cart.full_name} (${cart.email})`;
    if (cart.full_name) return cart.full_name;
    if (cart.email) return cart.email;

    return (
      <span className="text-zinc-400 font-mono text-xs">
        Session: {cart.anonymous_id?.substring(0, 8)}...
      </span>
    );
  };

  if (loading) {
    return (
      <AppShell title="Abandoned Carts">
        <div className="flex justify-center p-12">
          <Spinner />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell
      title="Abandoned Carts"
      subtitle="View uncompleted checkouts and recover leads."
    >
      <div className="p-4 sm:p-6 md:p-8 max-w-6xl mx-auto space-y-6">
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-zinc-500 bg-zinc-50 uppercase tracking-widest border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 font-semibold">User / Session</th>
                  <th className="px-4 py-3 font-semibold">Plan</th>
                  <th className="px-4 py-3 font-semibold">Total</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Last Activity</th>
                  <th className="px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-zinc-200">
                {carts.length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-8 text-center text-zinc-500"
                    >
                      No abandoned carts found.
                    </td>
                  </tr>
                ) : (
                  carts.map((cart) => (
                    <tr
                      key={cart.id}
                      className="hover:bg-zinc-50 cursor-pointer transition-colors"
                      onClick={() => setSelectedCart(cart)}
                    >
                      <td className="px-4 py-3 font-medium text-zinc-950">
                        {getIdentity(cart)}
                      </td>

                      <td className="px-4 py-3 text-zinc-600">
                        {cart.plan}{" "}
                        <span className="text-xs text-zinc-400">
                          ({cart.lead_types?.length || 0} types)
                        </span>
                      </td>

                      <td className="px-4 py-3 font-semibold text-zinc-950">
                        {formatPrice(cart.total_amount || 0)}
                        <span className="text-xs text-zinc-500 font-normal">
                          /{cart.billing === "yearly" ? "yr" : "mo"}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        {getStatusBadge(cart.status)}
                      </td>

                      <td className="px-4 py-3 text-zinc-500">
                        {cart.last_activity_at
                          ? formatDistanceToNow(
                              new Date(cart.last_activity_at),
                              { addSuffix: true }
                            )
                          : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            abandonedCartApi.discardCart(cart.id).then(() => {
                              setCarts((prev) =>
                                prev.filter((c) => c.id !== cart.id)
                              );
                              if (selectedCart?.id === cart.id) {
                                setSelectedCart(null);
                              }
                            });
                          }}
                        >
                          X
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {selectedCart && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          onClick={() => setSelectedCart(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 bg-zinc-50/50">
              <h2 className="text-lg font-bold text-zinc-950">
                Cart Details
              </h2>

              <button
                onClick={() => setSelectedCart(null)}
                className="p-1 rounded-md text-zinc-400 hover:text-zinc-950 hover:bg-zinc-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">
              {/* TOP GRID FIXED */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-lg border border-zinc-150 bg-zinc-40 p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Identity
                  </p>
                  <p className="text-sm font-medium text-zinc-950 break-words">
                    {getIdentity(selectedCart)}
                  </p>
                  {selectedCart.phone_number && (
                    <p className="text-sm text-zinc-600 mt-1">
                      {selectedCart.phone_number}
                    </p>
                  )}
                </div>

                <div className="rounded-lg border border-zinc-150 bg-zinc-40 p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    State
                  </p>
                  <p className="text-sm font-medium text-zinc-950">
                    {selectedCart.cart_data?.state_code || "None"}
                  </p>
                </div>

                <div className="rounded-lg border border-zinc-150 bg-zinc-40 p-4">
                  <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                    Status
                  </p>

                  <div className="space-y-2">
                    {getStatusBadge(selectedCart.status)}

                    <div className="text-xs text-zinc-500">
                      Created:{" "}
                      {new Date(
                        selectedCart.created_at
                      ).toLocaleString()}
                    </div>

                    {selectedCart.abandoned_at && (
                      <div className="text-xs text-zinc-500">
                        Abandoned:{" "}
                        {new Date(
                          selectedCart.abandoned_at
                        ).toLocaleString()}
                      </div>
                    )}

                    {selectedCart.completed_at && (
                      <div className="text-xs text-zinc-500">
                        Completed:{" "}
                        {new Date(
                          selectedCart.completed_at
                        ).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ITEMS */}
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
                  Selected Items
                </p>

                <div className="bg-zinc-50 rounded-md border border-zinc-200 p-4 space-y-3">
                  <div className="flex justify-between items-center pb-2 border-b border-zinc-200">
                    <span className="font-semibold text-zinc-950">
                      {selectedCart.plan}
                    </span>
                    <span className="font-bold">
                      {formatPrice(selectedCart.total_amount || 0)}/
                      {selectedCart.billing === "yearly" ? "yr" : "mo"}
                    </span>
                  </div>

                  {selectedCart.lead_types?.map((lt, idx) => (
                    <div key={idx} className="text-sm">
                      <span className="font-medium text-zinc-700 capitalize">
                        {lt?.label || lt?.name || lt || "Unknown"}
                      </span>
                    </div>
                  ))}

                  <div className="pt-2">
                    <span className="text-xs font-semibold text-zinc-500">
                      Counties:{" "}
                    </span>
                    <span className="text-sm text-zinc-700">
                      {selectedCart.counties
                        ?.map((c) => c?.name || c)
                        .join(", ") || "None"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-zinc-100 flex justify-end">
              <Button
                variant="secondary"
                onClick={() => setSelectedCart(null)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}